import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchAssetContent,
  fetchAssets,
  filterByCategory,
  resolveAssetUrl,
  staticFileToAssetReference,
  uploadAsset,
  type AssetApi,
} from './assetService';
import { injectNewAssetFontFace } from './legacyAssets';
import type { AssetCategory, StaticFile } from './types';

const PAGE_SIZE = 8;
const FETCH_BATCH_SIZE = 50;
const MIN_DISPLAY_COUNT = 10;

type AssetWithUrl = StaticFile & { resolvedUrl?: string; fontFamily?: string };

export function useAssetBrowser({ isOpen, category, organizationId, api }: {
  isOpen: boolean;
  category: AssetCategory;
  organizationId: number;
  api: AssetApi;
}) {
  const [assets, setAssets] = useState<AssetWithUrl[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(-1);
  const [hasMore, setHasMore] = useState(true);
  const loadRequestIdRef = useRef(0);
  const uploadRequestIdRef = useRef(0);

  const invalidate = useCallback(() => {
    loadRequestIdRef.current += 1;
    uploadRequestIdRef.current += 1;
    setLoading(false);
    setLoadingMore(false);
    setUploading(false);
  }, []);

  const loadAssets = useCallback(async (initial: boolean) => {
    const requestId = ++loadRequestIdRef.current;
    if (initial) {
      setLoading(true);
      setLoadingMore(false);
      setAssets([]);
      setPage(-1);
    } else {
      setLoadingMore(true);
    }
    setError(null);

    try {
      let currentPage = initial ? 0 : page + 1;
      let nextAssets: AssetWithUrl[] = initial ? [] : [...assets];
      let moreData = true;
      const targetCount = initial ? MIN_DISPLAY_COUNT : nextAssets.length + PAGE_SIZE;

      while (nextAssets.length < targetCount && moreData) {
        const batch = await fetchAssets(api, organizationId, currentPage, FETCH_BATCH_SIZE);
        if (requestId !== loadRequestIdRef.current) return;
        if (batch.length < FETCH_BATCH_SIZE) moreData = false;
        const filtered = filterByCategory(batch, category);
        const processed = await Promise.all(filtered.map(async (asset) => {
          try {
            if (category === 'image') {
              const resolvedUrl = await resolveAssetUrl(api, organizationId, asset.blobName);
              return requestId === loadRequestIdRef.current ? { ...asset, resolvedUrl } : asset;
            }
            const content = await fetchAssetContent(
              api,
              organizationId,
              asset.blobName,
              () => localStorage.getItem('rid.jwt'),
            );
            if (requestId !== loadRequestIdRef.current || !content) return asset;
            return { ...asset, fontFamily: injectNewAssetFontFace(staticFileToAssetReference(asset), content) };
          } catch {
            return asset;
          }
        }));
        if (requestId !== loadRequestIdRef.current) return;
        nextAssets = [...nextAssets, ...processed];
        setAssets(nextAssets);
        setPage(currentPage);
        setHasMore(moreData);
        currentPage += 1;
      }

      if (requestId === loadRequestIdRef.current) {
        setAssets(nextAssets);
        setPage(currentPage - 1);
        setHasMore(moreData);
      }
    } catch (nextError) {
      if (requestId !== loadRequestIdRef.current) return;
      console.error('[AssetBrowser] Failed to load assets:', nextError);
      setError('Failed to load assets. Please try again.');
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [api, organizationId, category, page, assets]);

  useEffect(() => {
    invalidate();
    setAssets([]);
    setPage(-1);
    setHasMore(true);
    setError(null);
    if (isOpen) loadAssets(true);
    return invalidate;
  }, [isOpen, api, organizationId, category]);

  const upload = useCallback(async (file: File) => {
    const requestId = ++uploadRequestIdRef.current;
    // Supersede any picker read as soon as upload starts, so an older read
    // cannot overwrite the uploaded asset's authoritative refresh.
    loadRequestIdRef.current += 1;
    setLoading(false);
    setLoadingMore(false);
    setUploading(true);
    setError(null);
    try {
      await uploadAsset(api, organizationId, file);
      if (requestId !== uploadRequestIdRef.current) return;
      await loadAssets(true);
    } catch (nextError) {
      if (requestId !== uploadRequestIdRef.current) return;
      console.error('[AssetBrowser] Upload failed:', nextError);
      setError('Failed to upload file. Please try again.');
    } finally {
      if (requestId === uploadRequestIdRef.current) setUploading(false);
    }
  }, [api, organizationId, loadAssets]);

  return {
    assets,
    loading,
    loadingMore,
    uploading,
    error,
    hasMore,
    loadMore: () => loadAssets(false),
    upload,
    invalidate,
    setError,
  };
}
